import { TxSearchResult, type TxSummary } from '@apm/contracts';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getJson, toQuery } from '../api';
import { TX_TYPE_TONE } from '../live/format';
import {
  currentQuery,
  mergeQuery,
  navigate,
  omitKeys,
  replaceQuery,
  routeHref,
  useCurrentQuery,
} from '../router';
import { useServiceFilter } from '../services/service-filter';
import { STATUS_LEVEL_TEXT, statusLevel, statusText } from '../status';
import { useLiveSelector } from '../store/useStream';
import { TIP, TX_TYPE_OPTIONS } from '../terms';
import { formatCount, formatDuration, formatFull, formatStamp } from '../time';
import {
  Button,
  Cell,
  Chip,
  type Column,
  EmptyState,
  ErrorText,
  Field,
  RangePicker,
  Row,
  Section,
  Select,
  Table,
  TextInput,
  Toggle,
} from '../ui';
import { downloadCsv } from '../xview/csv';
import { XVIEW_QUERY_KEYS } from '../xview/XViewPanel';
import {
  appendCapped,
  applySseFilter,
  defaultForm,
  exactRange,
  formFromParams,
  SEARCH_ROWS_LIMIT,
  type SearchForm,
  toAddressUpdates,
  toSearchParams,
  validateForm,
} from './query';

/**
 * 기간 지정 검색 (7회차 결정 4). 요청 화면의 아래쪽 구획이다 (22-B회차).
 *
 * 분포도는 최근 5분만 본다. 어제 그 요청을 찾으려면 저장 파일을 봐야 하고,
 * 그 길이 `GET /api/tx/search`다. 결과 행을 누르면 상세로 간다.
 *
 * 더 보기는 커서를 그대로 돌려주는 방식이라 결과가 쌓이는 동안에도 행이 겹치지 않는다.
 *
 * 주소에 조건이 실려 오면(`#/search?…`) 그것으로 폼을 채우고 한 번 바로 조회한다.
 * 19회차부터 **찾을 때 주소도 같이 바뀐다**(후보 #8). 지금 보는 조건을 그대로 붙여 넣어
 * 남에게 보낼 수 있다. 22-B회차에 결과 CSV 내려받기가 붙었다(빠진 정보 7) —
 * 열은 분포도에서 끌어 고른 목록의 CSV와 같다.
 */

const PAGE_SIZE = 200;

const COLUMNS: readonly Column[] = [
  { label: '시작', width: 'w-40' },
  { label: '인스턴스', width: 'w-40', title: TIP.instance },
  { label: '유형', width: 'w-28' },
  { label: '요청 경로', title: TIP.entry },
  { label: '응답시간', width: 'w-28' },
  { label: '상태', width: 'w-32' },
  { label: 'DB 오류', width: 'w-32' },
  { label: '회사(테넌트)', width: 'w-28', title: TIP.tenant },
];

export function SearchPanel({ query }: { query?: string } = {}) {
  const service = useServiceFilter();
  // 인스턴스 목록만 본다 (D-C1)
  const liveInstances = useLiveSelector((live) => live.instances);
  const [form, setForm] = useState<SearchForm>(() => {
    const parsed = query === undefined ? defaultForm() : formFromParams(query);
    return { ...parsed, service: service === '' ? parsed.service : service };
  });
  const [rows, setRows] = useState<TxSummary[]>([]);
  /** 상한을 넘어 표에서 뺀 누적 수 (D-C3). 새 검색이면 0 이다 */
  const [dropped, setDropped] = useState(0);
  const [result, setResult] = useState<TxSearchResult>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [searched, setSearched] = useState(false);
  /**
   * 지금 도는 검색 (44회차). 새 검색이 앞의 것을 끊고, 늦게 온 옛 응답은 버린다. 43회차까지는 취소가
   * 없어, 서비스를 바꾸는 사이 먼저 보낸 검색이 늦게 오면 옛 조건의 결과가 새 조건 화면에 남았다
   */
  const inflight = useRef<AbortController | undefined>(undefined);
  useEffect(() => () => inflight.current?.abort(), []);

  /**
   * `override`는 주소로 들어온 조건. 폼 상태가 반영되기 전에 그대로 조회한다.
   * `exact`는 주소의 원래 기간이다 — 폼의 `datetime-local`은 ms를 담지 못해, 추이 구간이나
   * 앞뒤 요청 링크가 준 기간이 첫 조회에서 어긋난다 (23-A회차 리뷰 6).
   */
  const run = async (
    cursor?: string,
    override?: SearchForm,
    exact?: { from?: number; to?: number },
  ) => {
    const source = override ?? form;
    // 전체 보기의 인스턴스는 서비스와 한 쌍이다. 그때만 폼의 서비스를 서버 조건으로 쓴다.
    const current = { ...source, service: service === '' ? source.service : service };
    // 새 검색을 시도한 순간 앞 검색은 뜻을 잃는다. 검증에 걸려도 앞 검색을 끊는다
    inflight.current?.abort();
    inflight.current = undefined;
    const invalid = validateForm(current);
    if (invalid !== undefined) {
      setError(invalid);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    inflight.current = controller;
    setLoading(true);
    setError(undefined);
    try {
      const params = toSearchParams(current, PAGE_SIZE, cursor);
      if (exact?.from !== undefined) params.from = exact.from;
      if (exact?.to !== undefined) params.to = exact.to;
      const url = `/api/tx/search${toQuery(params)}`;
      const next = await getJson(url, TxSearchResult, controller.signal);
      if (controller.signal.aborted) return;
      setResult(next);
      if (cursor === undefined) {
        setRows(next.rows);
        setDropped(0);
      } else {
        // 더 보기로 쌓는 것은 상한까지만 둔다. 넘으면 먼저 받은 것부터 뺀다 (D-C3)
        const capped = appendCapped(rows, next.rows, dropped);
        setRows(capped.rows);
        setDropped(capped.dropped);
      }
      setSearched(true);
      // 조건을 주소에 남긴다. 분포도의 `x…` 키는 건드리지 않는다 (23-A회차 리뷰 3)
      if (cursor === undefined) {
        const updates = toAddressUpdates({ ...current, service: '' });
        // 전체 보기의 인스턴스는 이름만으로 모호하다. 전역 service와 섞이지 않는 키로 쌍을 남긴다.
        updates.instanceService = service === '' ? current.service || undefined : undefined;
        const nextQuery = mergeQuery(currentQuery(), updates);
        // 아래 replace가 만든 주소를 주소 진입으로 다시 처리하지 않는다. 비교는 제 키끼리다
        ranFor.current = omitKeys(nextQuery, XVIEW_QUERY_KEYS);
        replaceQuery({
          name: 'requests',
          query: nextQuery,
        });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      // 끊긴 검색은 뒤를 이은 검색이 도는 중이다. 읽는 중 표시는 그쪽이 끈다
      if (inflight.current === controller) {
        inflight.current = undefined;
        setLoading(false);
      }
    }
  };

  const update = (patch: Partial<SearchForm>) => setForm((prev) => ({ ...prev, ...patch }));

  // 주소로 들어온 조건은 사람이 단추를 누르기 전에 한 번 돌린다. 같은 조건으로는 다시 돌지 않는다.
  // **제 키만 본다** (44회차). 분포도가 `x…` 키를 갈아 끼우면 실시간 프레임의 다시 그리기가 새 주소를
  // 내려보내는데, 그것을 주소 진입으로 보면 거르기만 켜도 검색이 다시 돌고 고치던 조건이 덮인다
  const ranFor = useRef<string | undefined>(undefined);
  const ownQuery = query === undefined ? undefined : omitKeys(query, XVIEW_QUERY_KEYS);
  useEffect(() => {
    if (ownQuery === undefined || ownQuery === '' || ranFor.current === ownQuery) return;
    ranFor.current = ownQuery;
    const parsed = formFromParams(ownQuery);
    const next = { ...parsed, service: service === '' ? parsed.service : service };
    setForm(next);
    void run(undefined, next, exactRange(ownQuery));
  }, [ownQuery]);

  // 같은 검색 조건으로 서비스만 바꿀 때 결과를 다시 읽는다. 인스턴스는 서비스에 매인 값이라 비운다.
  const previousService = useRef(service);
  useEffect(() => {
    if (previousService.current === service) return;
    previousService.current = service;
    const next = { ...form, service, instance: '' };
    setForm(next);
    // 첫 검색이 아직 도는 중이어도 다시 읽는다. 그 결과는 옛 서비스 것이다
    if (searched || inflight.current !== undefined) void run(undefined, next);
  }, [service]);

  const shown = useMemo(() => applySseFilter(rows, form.includeSse), [rows, form.includeSse]);
  // 결과 표는 `memo`라 실시간 반영(인스턴스 목록)으로 이 화면이 다시 그려져도 따라 그리지 않는다.
  // 더 보기는 늘 마지막으로 그린 검색 조건으로 돈다
  const runRef = useRef(run);
  runRef.current = run;
  const more = useCallback((cursor?: string) => void runRef.current(cursor), []);
  // 결과 표 링크의 복귀 조건. 분포도가 `x…` 키를 바꿔도 따라간다 (R62-2)
  const back = useCurrentQuery();
  const instances = useMemo(
    () => instanceOptions(liveInstances, rows, service, form),
    [liveInstances, rows, service, form.service, form.instance],
  );
  const instanceValue =
    form.instance === ''
      ? ''
      : service === ''
        ? instanceKey(form.service, form.instance)
        : form.instance;

  return (
    <>
      <Section
        boxed
        title="요청 검색"
        note="저장된 요청을 조건으로 검색한다. 최대 31일, 행을 누르면 상세로 간다"
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void run();
          }}
        >
          <RangePicker
            idPrefix="search"
            value={{ from: form.from, to: form.to }}
            onChange={(range) => update(range)}
          />

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label={form.entryExact ? '요청 경로 (정확히 일치)' : '요청 경로 (부분 일치)'}
              htmlFor="search-entry"
              hint={TIP.entry}
            >
              <TextInput
                id="search-entry"
                value={form.entry}
                onChange={(entry) => update({ entry })}
                placeholder="/api/v2/proposals"
              />
              <Toggle
                id="search-entry-exact"
                checked={form.entryExact}
                onChange={(entryExact) => update({ entryExact })}
                label="정확히 일치"
                hint={
                  '통계·추이에서 넘어온 조건은 이쪽이다.\n' +
                  '끄면 경로에 그 글자가 든 요청을 전부 찾는다'
                }
              />
            </Field>
            <Field label="유형" htmlFor="search-txtype">
              <Select
                id="search-txtype"
                value={form.txType}
                onChange={(value) => update({ txType: value as SearchForm['txType'] })}
                options={[
                  { value: '', label: '전체' },
                  ...TX_TYPE_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
                ]}
              />
            </Field>
            <Field label="상태" htmlFor="search-status">
              <Select
                id="search-status"
                value={form.status}
                onChange={(value) => update({ status: value as SearchForm['status'] })}
                options={[
                  { value: '', label: '전체' },
                  { value: 'ok', label: '정상' },
                  { value: 'fail', label: '실패 (4xx·DB 오류)' },
                  { value: 'error', label: '오류 (5xx·예외)' },
                ]}
              />
            </Field>
            <Field label="최소 응답시간 (ms)" htmlFor="search-min-duration">
              <TextInput
                id="search-min-duration"
                value={form.minDurationMs}
                onChange={(minDurationMs) => update({ minDurationMs })}
                inputMode="numeric"
                placeholder="0"
              />
            </Field>
            <Field label="인스턴스" htmlFor="search-instance" hint={TIP.instance}>
              <Select
                id="search-instance"
                value={instanceValue}
                onChange={(value) => {
                  const picked = instances.find((item) => item.value === value);
                  update({
                    service: service === '' ? (picked?.service ?? '') : service,
                    instance: picked?.instance ?? '',
                  });
                }}
                options={instances.map(({ value, label }) => ({ value, label }))}
              />
            </Field>
            <Field label="회사(테넌트)" htmlFor="search-tenant" hint={TIP.tenant}>
              <TextInput
                id="search-tenant"
                value={form.tenantId}
                onChange={(tenantId) => update({ tenantId })}
              />
            </Field>
            <Field label="클라이언트 IP" htmlFor="search-client">
              <TextInput
                id="search-client"
                value={form.clientAddress}
                onChange={(clientAddress) => update({ clientAddress })}
                placeholder="10.0.0.1"
              />
            </Field>
            <Field label="브라우저 (부분 일치)" htmlFor="search-user-agent" hint={TIP.userAgent}>
              <TextInput
                id="search-user-agent"
                value={form.userAgent}
                onChange={(userAgent) => update({ userAgent })}
                placeholder="Mozilla/5.0"
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? '검색 중…' : '검색'}
            </Button>
            <Button
              onClick={() => {
                // 도는 검색도 끊는다. 늦게 온 결과가 비운 화면을 다시 채우지 않게
                inflight.current?.abort();
                inflight.current = undefined;
                setLoading(false);
                setForm({ ...defaultForm(), service });
                setRows([]);
                setDropped(0);
                setResult(undefined);
                setSearched(false);
                setError(undefined);
              }}
            >
              조건 초기화
            </Button>
            <Toggle
              id="search-include-sse"
              checked={form.includeSse}
              onChange={(includeSse) => update({ includeSse })}
              label="실시간 연결 포함"
              hint={TIP.sseExcluded}
            />
          </div>
        </form>

        {error !== undefined ? (
          <div className="mt-3">
            <ErrorText>{error}</ErrorText>
          </div>
        ) : null}
      </Section>

      {searched ? (
        <Results
          rows={shown.rows}
          hiddenSse={shown.hidden}
          dropped={dropped}
          back={back}
          result={result}
          loading={loading}
          onMore={more}
          showService={service === ''}
        />
      ) : null}
    </>
  );
}

const Results = memo(function Results({
  rows,
  hiddenSse,
  dropped,
  back,
  result,
  loading,
  onMore,
  showService,
}: {
  rows: TxSummary[];
  hiddenSse: number;
  dropped: number;
  /** 상세 링크에 실을 복귀 조건 (R62-2) */
  back?: string;
  result?: TxSearchResult;
  loading: boolean;
  onMore: (cursor?: string) => void;
  showService: boolean;
}) {
  const note = [
    `전체 ${formatCount(result?.total ?? 0)}건 중 ${formatCount(rows.length)}건 표시`,
    hiddenSse > 0 ? `실시간 연결 ${formatCount(hiddenSse)}건 제외` : '',
    result?.truncated === true ? TIP.truncated : '',
  ]
    .filter((part) => part !== '')
    .join(' · ');

  return (
    <Section
      boxed
      title="검색 결과"
      note={note}
      badge={result?.days !== undefined ? `날짜 폴더 ${result.days}개 읽음` : undefined}
      actions={
        <>
          {rows.length > 0 ? (
            <Button
              size="sm"
              onClick={() => downloadCsv(rows)}
              title="지금 표에 보이는 행을 그대로 받는다. 더 보기로 받은 것까지 포함한다"
            >
              CSV 내려받기
            </Button>
          ) : null}
          {result?.nextCursor !== undefined ? (
            <Button size="sm" onClick={() => onMore(result.nextCursor)} disabled={loading}>
              {loading ? '읽는 중…' : '더 보기'}
            </Button>
          ) : null}
        </>
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          title="조건에 맞는 요청이 없다"
          hint={
            hiddenSse > 0
              ? "실시간 연결만 있다. '실시간 연결 포함'을 켜면 보인다"
              : '기간을 넓히거나 요청 경로 조건을 지울 것'
          }
        />
      ) : (
        <Table
          columns={showService ? [{ label: '서비스', width: 'w-40' }, ...COLUMNS] : COLUMNS}
          caption="검색 결과"
          minWidth={showService ? 'min-w-[1280px]' : 'min-w-[1120px]'}
          maxHeight="max-h-[640px]"
        >
          {rows.map((row) => (
            // 행 전체의 누르기는 마우스 편의로 남기고, 키보드·보조기술은 첫 칸의 링크로 간다 (13회차)
            <Row
              key={row.txId}
              onClick={() =>
                navigate({
                  name: 'detail',
                  traceId: row.traceId,
                  from: 'requests',
                  service: row.service,
                  // 그 줄을 바로 열고 서버가 그날 앞뒤 파일만 먼저 보게 한다 (59회차 이월)
                  txId: row.txId,
                  at: row.startedAt,
                  back: currentQuery(),
                })
              }
            >
              {showService ? (
                <Cell title={row.service} className="max-w-[180px] truncate">
                  {row.service}
                </Cell>
              ) : null}
              <Cell nowrap tone="muted" className="tabular">
                <a
                  href={routeHref({
                    name: 'detail',
                    traceId: row.traceId,
                    from: 'requests',
                    service: row.service,
                    txId: row.txId,
                    at: row.startedAt,
                    back,
                  })}
                  title={formatFull(row.startedAt)}
                  className="-mx-1 block rounded px-1 text-inherit no-underline"
                >
                  {formatStamp(row.startedAt)}
                </a>
              </Cell>
              <Cell mono tone="muted" title={row.instance} className="max-w-[180px] truncate">
                {row.instance ?? '-'}
              </Cell>
              <Cell>
                <Chip tone={TX_TYPE_TONE[row.txType]}>
                  {TX_TYPE_OPTIONS.find((item) => item.value === row.txType)?.label ?? row.txType}
                </Chip>
              </Cell>
              <Cell mono title={row.entry} className="max-w-[340px] truncate">
                {row.entry}
              </Cell>
              <Cell mono nowrap className="tabular">
                {formatDuration(row.durationMs)}
              </Cell>
              <Cell nowrap className={STATUS_LEVEL_TEXT[statusLevel(row)]}>
                {statusText(row)}
              </Cell>
              <Cell nowrap className="tabular">
                {row.sqlErrorCount > 0 ? (
                  <span className="text-error">
                    {row.sqlErrorCount}건{row.sqlErrorCode ? ` (${row.sqlErrorCode})` : ''}
                  </span>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </Cell>
              <Cell tone="muted" className="tabular">
                {row.tenantId ?? '-'}
              </Cell>
            </Row>
          ))}
        </Table>
      )}
      {dropped > 0 ? (
        <p className="tabular mt-3 text-xs text-fail">
          표에는 {formatCount(SEARCH_ROWS_LIMIT)}건까지만 둔다. 먼저 받은 {formatCount(dropped)}건은
          뺐고 CSV에도 들어가지 않는다
        </p>
      ) : null}
    </Section>
  );
});

interface InstanceOption {
  value: string;
  label: string;
  service: string;
  instance: string;
}

function instanceKey(service: string, instance: string): string {
  return JSON.stringify([service, instance]);
}

/** 현재 보고 중인 인스턴스와 이미 받은 결과를 합친다. 전체에서는 서비스까지 묶어 구분한다. */
function instanceOptions(
  live: readonly { service: string; instance: string }[],
  rows: readonly TxSummary[],
  service: string,
  form: SearchForm,
): InstanceOption[] {
  const found = new Map<string, InstanceOption>();
  const add = (itemService: string, instance: string | undefined) => {
    if (instance === undefined || instance === '') return;
    if (service !== '' && itemService !== service) return;
    const value = service === '' ? instanceKey(itemService, instance) : instance;
    found.set(value, {
      value,
      label: service === '' ? `${itemService} / ${instance}` : instance,
      service: itemService,
      instance,
    });
  };
  for (const item of live) add(item.service, item.instance);
  for (const row of rows) add(row.service, row.instance);
  if (form.instance !== '' && (service !== '' || form.service !== '')) {
    add(service === '' ? form.service : service, form.instance);
  }
  return [
    { value: '', label: '전체', service: '', instance: '' },
    ...[...found.values()].sort((a, b) => a.label.localeCompare(b.label)),
  ];
}
